import React from 'react';
import { FaPlay, FaSpinner } from 'react-icons/fa';
import config from '../../../config.js';
import { useT } from '../../../contexts/LocaleContext.jsx';
import { useBroadcastSurface } from '../../../contexts/BroadcastSurface.jsx';
import { EMBED_ALLOW, TWITCH_EMBED_ALLOW, isSafeEmbedUrl, mutedEmbedUrl } from '../../../_helpers/streamSource.js';
import '../../../styles/VideoPlayer.css';

/**
 * `streamUrl` is the player URL the server built from the band's configured
 * stream source (`embedUrl` on `/public/bands/{id}/stream/status`) and is the
 * single source of truth when present. `twitchChannel` (a Twitch channel found
 * in the band's social links) is only a fallback for bands with no configured
 * source, so a band that streams elsewhere never gets its old Twitch offline
 * screen instead of the live show.
 *
 * Playback (play/pause, volume, fullscreen) is owned entirely by the embedded
 * player: the iframe cannot be driven from outside, and Twitch/YouTube already
 * render their own controls. That makes the embed's fullscreen button the only
 * fullscreen affordance, so both iframes must let the player go fullscreen;
 * their `allow` lists are the shared EMBED_ALLOW / TWITCH_EMBED_ALLOW from
 * _helpers/streamSource.js, which also explains why `fullscreen` is declared
 * there explicitly alongside `allowfullscreen` (ENG-340, ENG-347). SongShake
 * draws no controls of its own on or around the player (ENG-766): the frame
 * is followed directly by whatever the page renders next.
 *
 * Nothing of SongShake's may sit *over* the iframe. Twitch's player only
 * autoplays while its frame passes IntersectionObserver v2's `isVisible`
 * check, which fails when any other element paints on top of the frame (or an
 * ancestor carries opacity < 1, a filter, a blend mode or a non-trivial
 * transform); the player then logs "requirements ... not met: style
 * visibility" and falls back to a click-to-play poster. Two things used to
 * trip it: the 0.5s entrance fade on the page grid (an ancestor, so it failed
 * every player that mounted during it — the likely desktop cause; see
 * StreamPage.css), and SongShake chrome painted inside the frame's container.
 * `.video-player-container` therefore holds the frame and nothing else
 * (ENG-344), and `.video-player` holds only that container.
 *
 * `muted` starts the player silent (the provider's own parameter, see
 * mutedEmbedUrl). It is for frames that show a band its own stream, such as
 * the streamer dashboard's preview, where audio would feed back into the room.
 */
const VideoPlayer = ({ bandId, twitchChannel, isLoading = false, error = null, streamUrl = null, muted = false }) => {
  const t = useT();
  // While it shows a video frame, app-level overlays must not cover this page.
  useBroadcastSurface(!isLoading && !error && (isSafeEmbedUrl(streamUrl) || Boolean(twitchChannel)));

  if (isLoading) {
    return (
      <div className="video-player loading">
        <div className="video-player-loading" role="status">
          <FaSpinner className="loading-spinner" aria-hidden="true" />
          <p>{t('stream.player.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="video-player error" data-testid="stream-offline">
        <div className="video-player-error">
          <h3>{t('stream.unavailable.title')}</h3>
          <p>{error}</p>
          <button className="retry-button" onClick={() => window.location.reload()}>
            {t('common.actions.retry')}
          </button>
        </div>
      </div>
    );
  }

  const embedUrl = isSafeEmbedUrl(streamUrl) ? (muted ? mutedEmbedUrl(streamUrl) : streamUrl) : null;

  return (
    <div className="video-player">
      <div className="video-player-container">
        {embedUrl ? (
          <iframe
            className="video-embed-frame"
            data-testid="stream-embed"
            data-embed-kind="source"
            src={embedUrl}
            title={t('stream.player.frameTitle')}
            frameBorder="0"
            sandbox="allow-scripts allow-same-origin allow-popups allow-presentation"
            allow={EMBED_ALLOW}
            allowFullScreen
          />
        ) : twitchChannel ? (
          <iframe
            src={`https://player.twitch.tv/?channel=${encodeURIComponent(twitchChannel)}&parent=${encodeURIComponent(config.twitchParentDomain || window.location.hostname)}&autoplay=true${muted ? '&muted=true' : ''}`}
            className="twitch-player-iframe"
            data-testid="stream-embed"
            data-embed-kind="twitch-channel"
            allowFullScreen
            allow={TWITCH_EMBED_ALLOW}
            title={t('stream.player.twitchFrameTitle', { channel: twitchChannel })}
          />
        ) : (
          <div className="video-placeholder" data-testid="stream-no-embed">
            <div className="video-placeholder-content">
              <FaPlay className="video-placeholder-icon" />
              <h3>{t('stream.player.placeholderTitle')}</h3>
              <p>{t('stream.player.streamingFrom', { bandId })}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoPlayer;
